'use client';

/**
 * OnboardingFlow
 *
 * The app's most important touchpoint: the moment a reader becomes a
 * participant. A short, guided, app-style flow (NOT a text-heavy landing) that
 * forks on a person's RELATIONSHIP to a project/community, then tailors a light
 * "what you get" payoff, then routes each branch to its live surface.
 *
 *   Screen 0 · Fork      — "What brings you here?"  (skipped on contributor entry)
 *   Screen 1 · Which + payoff — name the project + a shown-not-told glimpse
 *   Screen 2 · The ask   — builder: keep-warm email capture (secondary — the
 *                          primary CTA is /coverage); member: waitlist capture
 *                          while the contributor funnel is dark
 *   Screen 3 · Done      — celebratory finish
 *
 * Entry points:
 *   - `/join` mounts entry="fork" (starts at Screen 0).
 *   - entry="contributor" starts at the member-branch capture with the project
 *     prefilled (historically the /submit post-submit moment).
 *
 * BRANCH REALITY (2026-07-09): the two branches now live in different eras.
 *   - builder: the money path is LIVE — coverage is public (/coverage, no
 *     prices since 2026-10-06) and applying takes minutes (/apply), desk live
 *     ≤48h of kickoff. The primary CTA feeds /coverage; email capture remains only as a "not ready yet"
 *     secondary.
 *   - member/contributor: still WSK-gated coming-soon — capture stays the
 *     primary ask. Nothing here grants access; go-lives are Lam-gated.
 */

import { useState, useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  Rocket,
  Users,
  ArrowRight,
  ArrowLeft,
  Check,
  Radar,
  Hand,
  PlusCircle,
  Link2,
  HelpCircle,
} from 'lucide-react';
import InterestCapture from './InterestCapture';
import DirectorySearch from '@/components/ui/DirectorySearch';
import { tagService } from '@/lib/tag-service';
import { entityUrl } from '@/lib/entity-url';
import type { Tag } from '@/types/tags';
import { isWskEnabledFlag } from '@/lib/wsk-visibility';

type Branch = 'builder' | 'member';
type InterestId = 'contributing' | 'claiming' | 'coverage';

interface OnboardingFlowProps {
  /** 'fork' (default) starts at the relationship fork; 'contributor' skips it. */
  entry?: 'fork' | 'contributor';
  /** Prefill the project/community (e.g. the entity they just suggested). */
  project?: string;
  /** Restart affordance (e.g. /submit "suggest another"). */
  onReset?: () => void;
  /** Where this flow lives — segments the signup. */
  source?: string;
}

/** Per-branch copy + capture mapping. */
const BRANCH: Record<
  Branch,
  {
    account: string;
    interests: InterestId[];
    namePrompt: string;
    namePlaceholder: string;
    eyebrow: string;
  }
> = {
  builder: {
    account: 'project accounts',
    interests: ['claiming', 'coverage'],
    namePrompt: 'Which project is yours?',
    namePlaceholder: 'Start typing a name…',
    eyebrow: 'Your project',
  },
  member: {
    account: 'contributor access',
    interests: ['contributing'],
    namePrompt: 'Which project or community are you here for?',
    namePlaceholder: 'A game, a token, a studio, a DAO…',
    eyebrow: 'What you’re here for',
  },
};

/**
 * Step indices. The two branches differ in LENGTH: the contributor (member) path
 * collapses the "name your project" step (WHICH) and goes straight to capture.
 *
 * The builder (owner) WHICH step has two forms, chosen by the WSK flip:
 *   - flag OFF → the legacy "name your project + payoff" waitlist step, then ASK.
 *   - flag ON  → an entity picker that routes the owner OUT to the dossier claim
 *                flow (/tags/<slug>#claim). Selecting an entity leaves /join, so
 *                the builder machine has no ASK on the flag-on path.
 * The step order is therefore computed per-render from the flag (see `stepOrder`).
 */
const FORK = 0;
const WHICH = 1;
const ASK = 2;
const DONE = 3;

/** Post-capture soft handoff — the live surface for this branch. */
const HANDOFF: Record<Branch, { href: string; label: string }> = {
  builder: { href: '/coverage', label: 'See how coverage works' },
  member: { href: '/contribute', label: 'See how contributing works' },
};

/* ───────────────────────── motion helpers ───────────────────────── */

function useScreenVariants() {
  const reduce = useReducedMotion();
  const dx = reduce ? 0 : 26;
  return {
    enter: (dir: number) => ({ opacity: 0, x: dir > 0 ? dx : -dx }),
    center: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.34, ease: [0.22, 1, 0.36, 1] as const },
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -dx : dx,
      transition: { duration: 0.18, ease: 'easeIn' as const },
    }),
  };
}

/** A staggered reveal for elements within a screen. */
function Reveal({
  i = 0,
  className,
  children,
}: {
  i?: number;
  className?: string;
  children: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: reduce ? 0 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.04 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ───────────────────────── shared pieces ───────────────────────── */

/** A shown-not-told preview of a project's record; personalizes to the typed name. */
function RecordGlimpse({ name, mode }: { name: string; mode: Branch }) {
  const title = name.trim() || (mode === 'builder' ? 'Your project' : 'The project');
  const rows = ['What it is', 'Who’s behind it', 'Latest activity'];
  const widths = ['72%', '54%', '84%'];
  return (
    <div className="rounded-lg border border-divider bg-ink/50 p-4">
      <div className="mb-3.5 flex items-center gap-2.5">
        <span className="h-2 w-2 flex-none rounded-full bg-chain-abstract shadow-[0_0_8px_rgba(79,156,249,0.65)]" />
        <span className="truncate font-display text-[15px] font-bold text-parchment">
          {title}
        </span>
        <span className="ml-auto inline-flex flex-none items-center gap-1 rounded-full border border-signal-green/30 bg-signal-green/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-signal-green-text">
          <Check size={9} strokeWidth={3} /> Sourced
        </span>
      </div>
      <div className="space-y-2.5">
        {rows.map((label, i) => (
          <div key={label} className="flex items-center gap-2.5">
            <Check size={12} className="flex-none text-scout-gold/70" />
            <span className="w-[92px] flex-none font-mono text-[9.5px] uppercase tracking-[0.08em] text-parchment-dim/70">
              {label}
            </span>
            <span
              className="h-1.5 rounded-full bg-parchment-faint"
              style={{ width: widths[i] }}
            />
          </div>
        ))}
      </div>
      <div className="mt-3.5 border-t border-divider pt-2.5 font-mono text-[9.5px] uppercase tracking-[0.1em] text-parchment-dim/50">
        Always current · readable by AI agents
      </div>
    </div>
  );
}

function ProjectField({
  value,
  onChange,
  branch,
}: {
  value: string;
  onChange: (v: string) => void;
  branch: Branch;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-parchment-dim/70">
        {BRANCH[branch].namePrompt}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={BRANCH[branch].namePlaceholder}
        className="w-full rounded border border-divider bg-ink px-3.5 py-2.5 text-sm text-parchment placeholder:text-parchment-dim/60 focus:border-scout-gold/60 focus:outline-none focus:ring-1 focus:ring-scout-gold/40"
      />
    </label>
  );
}

/* ───────────────────────── main component ───────────────────────── */

export default function OnboardingFlow({
  entry = 'fork',
  project: initialProject,
  onReset,
  source,
}: OnboardingFlowProps) {
  // The contributor (entry="contributor", from /submit) lands straight on the
  // capture — its path has no separate "name your project" step.
  const startStep = entry === 'contributor' ? ASK : FORK;
  const minStep = startStep;

  const [[step, dir], setStepDir] = useState<[number, number]>([startStep, 0]);
  const [branch, setBranch] = useState<Branch | null>(
    entry === 'contributor' ? 'member' : null,
  );
  const [project, setProject] = useState(initialProject ?? '');
  // Entity list for the builder claim picker (WSK-on). Lazy-fetched once the owner
  // fork is taken — the same public client read /submit's dedup uses. Fail-silent.
  const [entityTags, setEntityTags] = useState<Tag[] | null>(null);

  const screen = useScreenVariants();

  // WSK flip. Both branches go live together: member waitlist → "sign in and start",
  // builder "name your project" waitlist → the entity picker → dossier claim flow.
  // Flag OFF → both false → byte-identical to the waitlist copy below.
  const wskEnabled = isWskEnabledFlag();

  // Per-branch step order. Builder collapses to [FORK, WHICH] on the flag-on path —
  // picking an entity navigates OUT to the claim flow, so there's no ASK to reach.
  const stepOrder: Record<Branch, number[]> = {
    builder: wskEnabled ? [FORK, WHICH] : [FORK, WHICH, ASK],
    member: [FORK, ASK],
  };

  const paginate = (next: number) => setStepDir([next, next > step ? 1 : -1]);
  // Pick the branch AND jump to its first step after the fork (builder → picker/
  // payoff; member → straight to capture).
  const choose = (br: Branch) => {
    setBranch(br);
    setStepDir([stepOrder[br][1], 1]);
  };

  const sourceTag = source ?? (entry === 'contributor' ? 'post_submit' : 'join');
  const b = branch ?? 'member';
  const live = b === 'member' && wskEnabled;
  const builderClaimLive = b === 'builder' && wskEnabled;
  const cfg = BRANCH[b];
  const handoff = HANDOFF[b];

  // Fetch the entity list once the owner fork is taken on the flag-on path.
  useEffect(() => {
    if (!(wskEnabled && branch === 'builder') || entityTags) return;
    let cancelled = false;
    tagService
      .getAllEntityTags(300)
      .then((tags) => {
        if (!cancelled) setEntityTags(tags);
      })
      .catch(() => {
        /* fail-silent — the "not on Descout yet?" escape still routes to /submit */
      });
    return () => {
      cancelled = true;
    };
  }, [wskEnabled, branch, entityTags]);

  // Branch-aware step sequence (member skips WHICH) — drives the rail + back nav.
  const steps = stepOrder[b];
  const pos = steps.indexOf(step);
  const prevStep = pos > 0 ? steps[pos - 1] : steps[0];

  /* ── screen bodies ── */

  const Fork = (
    <>
      <Reveal i={0}>
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-scout-gold">
          Welcome to Descout
        </p>
      </Reveal>
      <Reveal i={1}>
        <h2 className="mt-2.5 font-display text-[26px] font-black leading-[1.1] tracking-tight text-parchment sm:text-[30px]">
          Descout covers the founders and projects worth knowing.
        </h2>
      </Reveal>
      <Reveal i={2}>
        <p className="mt-2.5 max-w-md font-body text-[14px] leading-relaxed text-parchment-dim">
          Sourced, structured, always current — readable by anyone who comes
          looking, even AI agents.
        </p>
      </Reveal>
      <Reveal i={3}>
        <p className="mb-3 mt-7 font-display text-[15px] font-bold text-parchment">
          What brings you here?
        </p>
      </Reveal>
      <Reveal i={4}>
        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              { branch: 'builder' as const, Icon: Rocket, title: 'It’s my project', blurb: 'Claim it free, or put an independent news desk on it.' },
              { branch: 'member' as const, Icon: Users, title: 'I’m part of a project or community', blurb: 'Add what you know, and follow what happens next.' },
            ]
          ).map(({ branch: br, Icon, title, blurb }) => (
            <button
              key={br}
              type="button"
              onClick={() => choose(br)}
              className="group flex flex-col items-start gap-3 rounded-lg border border-divider bg-ink/40 p-5 text-left transition-colors duration-200 hover:border-scout-gold/50 hover:bg-scout-gold-glow focus:outline-none focus-visible:border-scout-gold/60 focus-visible:ring-1 focus-visible:ring-scout-gold/40"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-scout-gold/30 bg-scout-gold-dim">
                <Icon size={18} className="text-scout-gold" />
              </span>
              <span className="font-display text-[15.5px] font-bold leading-snug text-parchment">
                {title}
              </span>
              <span className="font-body text-[13px] leading-relaxed text-parchment-dim">
                {blurb}
              </span>
              <span className="mt-auto inline-flex items-center gap-1.5 pt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim/60 transition-colors group-hover:text-scout-gold">
                Continue
                <ArrowRight
                  size={12}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </span>
            </button>
          ))}
        </div>
      </Reveal>
    </>
  );

  const WhichPayoff = (
    <>
      <Reveal i={0}>
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-scout-gold">
          {cfg.eyebrow}
        </p>
      </Reveal>
      <Reveal i={1} className="mt-3">
        <ProjectField value={project} onChange={setProject} branch={b} />
      </Reveal>

      <Reveal i={2} className="mt-5">
        <RecordGlimpse name={project} mode={b} />
      </Reveal>

      {b === 'builder' ? (
        <Reveal i={3} className="mt-4">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-lg border border-divider bg-ink/40 p-3.5">
              <div className="mb-1.5 flex items-center gap-2">
                <Hand size={14} className="text-parchment-dim" />
                <span className="font-display text-[13px] font-bold text-parchment">
                  Claim it — free
                </span>
              </div>
              <p className="font-body text-[12px] leading-relaxed text-parchment-dim">
                You and the community keep it current.
              </p>
            </div>
            <div className="rounded-lg border border-scout-gold/30 bg-scout-gold-glow p-3.5">
              <div className="mb-1.5 flex items-center gap-2">
                <Radar size={14} className="text-scout-gold" />
                <span className="font-display text-[13px] font-bold text-parchment">
                  We cover it — paid
                </span>
              </div>
              <p className="font-body text-[12px] leading-relaxed text-parchment-dim">
                An independent desk watches and writes it for you — live within
                48 hours of kickoff.
              </p>
            </div>
          </div>
        </Reveal>
      ) : (
        <Reveal i={3} className="mt-4">
          <div className="flex flex-wrap gap-2">
            {[
              { Icon: PlusCircle, label: 'Suggest a fact' },
              { Icon: Link2, label: 'Add a source' },
              { Icon: HelpCircle, label: 'Answer an open question' },
            ].map(({ Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-divider bg-ink/50 px-3 py-1.5 text-[12px] text-parchment"
              >
                <Icon size={13} className="text-scout-gold" />
                {label}
              </span>
            ))}
          </div>
          <p className="mt-3 font-body text-[12.5px] leading-relaxed text-parchment-dim">
            You point, we verify. Build a track record — and follow what you care
            about.
          </p>
        </Reveal>
      )}

      <Reveal i={4} className="mt-6 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            setBranch('member');
            setStepDir([ASK, 1]);
          }}
          className="font-mono text-[10px] uppercase tracking-[0.1em] text-parchment-dim/60 transition-colors hover:text-parchment"
        >
          ← I’m part of a community
        </button>
        {/* Primary: the live money path — /coverage → /apply. */}
        <a
          href="/coverage"
          data-testid="join-builder-coverage-cta"
          className="group inline-flex items-center gap-2 rounded bg-scout-gold px-5 py-2.5 text-sm font-semibold text-ink transition-all hover:bg-scout-gold/90"
        >
          See how coverage works
          <ArrowRight
            size={14}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </a>
      </Reveal>
      {/* Secondary: keep-warm capture for builders who aren’t ready to apply. */}
      <Reveal i={5} className="mt-3 text-right">
        <button
          type="button"
          data-testid="join-builder-capture-link"
          onClick={() => paginate(ASK)}
          className="font-body text-[12px] text-parchment-dim transition-colors hover:text-parchment"
        >
          Not ready to apply?{' '}
          <span className="text-scout-gold">Leave your email →</span>
        </button>
      </Reveal>
    </>
  );

  // Builder WHICH step, flag-on: pick your project → route to its dossier claim card.
  const BuilderClaim = (
    <div data-testid="join-builder-claim">
      <Reveal i={0}>
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-scout-gold">
          {cfg.eyebrow}
        </p>
      </Reveal>
      <Reveal i={1}>
        <h2 className="mt-2.5 font-display text-[24px] font-black leading-[1.15] tracking-tight text-parchment sm:text-[26px]">
          Which project is yours?
        </h2>
      </Reveal>
      <Reveal i={2}>
        <p className="mt-2 max-w-md font-body text-[13.5px] leading-relaxed text-parchment-dim">
          Find it below to open its dossier. Verify ownership there and you hold the
          operator channel — corrections, sources, and your record.
        </p>
      </Reveal>
      <Reveal i={3} className="mt-4">
        <div data-testid="join-builder-picker">
          <DirectorySearch
            tags={entityTags ?? []}
            mode="navigate"
            skin="dashboard"
            hotkey={false}
            inlineResults
            placeholder="Start typing your project’s name…"
            getNavigationUrl={(tag) => `${entityUrl(tag.slug)}#claim`}
          />
        </div>
      </Reveal>
      <Reveal i={4} className="mt-4">
        <p className="font-body text-[12.5px] leading-relaxed text-parchment-dim">
          Not on Descout yet?{' '}
          <a href="/submit" className="text-scout-gold transition-colors hover:text-scout-gold/80">
            Add it first
          </a>{' '}
          — then claim it. Want the desk on it instead?{' '}
          <a href="/coverage" className="text-scout-gold transition-colors hover:text-scout-gold/80">
            See how coverage works
          </a>
          .
        </p>
      </Reveal>
      <Reveal i={5} className="mt-6">
        <button
          type="button"
          onClick={() => {
            setBranch('member');
            setStepDir([ASK, 1]);
          }}
          className="font-mono text-[10px] uppercase tracking-[0.1em] text-parchment-dim/60 transition-colors hover:text-parchment"
        >
          ← I’m part of a community
        </button>
      </Reveal>
    </div>
  );

  const Ask = (
    <>
      <Reveal i={0}>
        <div className="mb-3 flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-scout-gold/60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-scout-gold" />
          </span>
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-scout-gold">
            {b === 'builder' ? 'Coverage is live' : live ? 'Now open' : 'Opening soon'}
          </span>
        </div>
      </Reveal>
      <Reveal i={1}>
        <h2 className="font-display text-[22px] font-black leading-tight tracking-tight text-parchment sm:text-[24px]">
          {b === 'builder'
            ? 'Not ready to apply just yet?'
            : 'Help fill in what’s missing.'}
        </h2>
      </Reveal>

      {/* Contributor payoff — collapsed onto the capture (no separate step). */}
      {b === 'member' && (
        <Reveal i={2} className="mt-3">
          <div className="flex flex-wrap gap-2">
            {[
              { Icon: PlusCircle, label: 'Suggest a fact' },
              { Icon: Link2, label: 'Add a source' },
              { Icon: HelpCircle, label: 'Answer an open question' },
            ].map(({ Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-divider bg-ink/50 px-3 py-1.5 text-[12px] text-parchment"
              >
                <Icon size={13} className="text-scout-gold" />
                {label}
              </span>
            ))}
          </div>
          <p className="mt-3 font-body text-[12.5px] leading-relaxed text-parchment-dim">
            You point, we verify. Build a track record — recognition, not money.
          </p>
        </Reveal>
      )}

      {/* Step-2 body. Builder: the money path is live — this capture is only a
          keep-warm for owners who aren't ready to apply, so it says so and keeps
          the road back to /coverage open. Member: the waitlist ask while the
          contributor funnel is dark (live path's sentence stays cut — the
          contribution ask lands once, in the heading). */}
      {!live && (
        <Reveal i={3}>
          <p className="mb-5 mt-3 max-w-md font-body text-[13.5px] leading-relaxed text-parchment-dim">
            {b === 'builder' ? (
              <>
                Leave your email and we’ll check in
                {project.trim() ? <> about <span className="text-parchment">{project.trim()}</span></> : null}.
                Whenever you’re ready,{' '}
                <a
                  href="/coverage"
                  className="text-scout-gold transition-colors hover:text-scout-gold/80"
                >
                  see how coverage works
                </a>{' '}
                — applying takes a few minutes, and most desks are live within 48
                hours of kickoff.
              </>
            ) : (
              <>
                Want first access{project.trim() ? <> for <span className="text-parchment">{project.trim()}</span></> : null}? Drop your email and we’ll
                reach out when it opens.
              </>
            )}
          </p>
        </Reveal>
      )}
      <Reveal i={4}>
        {live ? (
          <a
            href="/contribute"
            data-testid="join-live-cta"
            className="group mt-6 inline-flex items-center gap-2 rounded bg-scout-gold px-5 py-2.5 text-sm font-semibold text-ink transition-all hover:bg-scout-gold/90"
          >
            Sign in and start
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </a>
        ) : (
          <InterestCapture
            source={sourceTag}
            entityName={project.trim() || undefined}
            defaultInterests={cfg.interests}
            showInterestPicker={false}
            submitLabel={b === 'builder' ? 'Keep me posted' : 'Save my spot'}
            note={
              b === 'builder'
                ? 'No spam — we’ll only write about coverage for your project.'
                : undefined
            }
            onSuccess={() => paginate(DONE)}
          />
        )}
      </Reveal>
    </>
  );

  const Done = (
    <div className="py-2 text-center">
      <motion.span
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 18 }}
        className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-scout-gold/40 bg-scout-gold-dim"
      >
        <Check size={30} className="text-scout-gold" strokeWidth={2.5} />
      </motion.span>
      <Reveal i={1}>
        <h2 className="font-display text-[24px] font-black tracking-tight text-parchment">
          {b === 'builder' ? 'We’ll keep in touch.' : 'You’re on the list.'}
        </h2>
      </Reveal>
      <Reveal i={2}>
        <p className="mx-auto mt-2 max-w-sm font-body text-[13.5px] leading-relaxed text-parchment-dim">
          {b === 'builder' ? (
            <>
              We’ve noted your interest
              {project.trim() ? <> in coverage for <span className="text-parchment">{project.trim()}</span></> : null}.
              Whenever you’re ready, applying takes a few minutes — most desks
              are live within 48 hours of kickoff.
            </>
          ) : (
            <>
              We’ll reach out when {cfg.account} open
              {project.trim() ? <> — and we’ve noted you’re here for <span className="text-parchment">{project.trim()}</span></> : null}.
            </>
          )}
        </p>
      </Reveal>
      <Reveal i={3} className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {/* Soft handoff — learn more about the surface for this branch. */}
        <a
          href={handoff.href}
          className="group inline-flex items-center gap-2 rounded bg-scout-gold px-5 py-2.5 text-sm font-semibold text-ink transition-all hover:bg-scout-gold/90"
        >
          {handoff.label}
          <ArrowRight
            size={14}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </a>
        {onReset ? (
          <button
            type="button"
            onClick={() => {
              setProject('');
              onReset();
            }}
            className="font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim transition-colors hover:text-parchment"
          >
            ← Suggest another
          </button>
        ) : (
          // "Explore Descout" = into the product (the desk) — `/` is the marketing
          // homepage now (the old `/`→`/abstract` 307 is gone).
          <a
            href="/abstract/"
            className="font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim transition-colors hover:text-parchment"
          >
            Explore Descout →
          </a>
        )}
      </Reveal>
    </div>
  );

  const body =
    step === FORK
      ? Fork
      : step === WHICH
        ? builderClaimLive
          ? BuilderClaim
          : WhichPayoff
        : step === ASK
          ? Ask
          : Done;

  /* ── progress + back affordance (branch-aware: member has fewer steps) ── */
  const total = steps.length;
  const railPos = step >= DONE ? total : pos; // Done sits past the last node.
  const showBack = step > minStep && step < DONE;
  const counter = `0${Math.min(railPos + 1, total)} / 0${total}`;

  return (
    <div data-testid="onboarding-flow" className="mx-auto w-full max-w-[640px]">
      <div className="overflow-hidden rounded-xl border border-divider bg-surface">
        {/* Top rail: back/reset · progress · counter */}
        <div className="flex items-center justify-between gap-3 border-b border-divider px-5 py-3 sm:px-7">
          <div className="min-w-[120px]">
            {showBack ? (
              <button
                type="button"
                onClick={() => paginate(prevStep)}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-parchment-dim transition-colors hover:text-parchment"
              >
                <ArrowLeft size={12} /> Back
              </button>
            ) : onReset && step < DONE ? (
              <button
                type="button"
                onClick={() => {
                  setProject('');
                  onReset();
                }}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-parchment-dim transition-colors hover:text-parchment"
              >
                <ArrowLeft size={12} /> Suggest another
              </button>
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-parchment-dim/50">
                Descout
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {steps.map((s, i) => {
              const state = railPos > i ? 'done' : railPos === i ? 'current' : 'todo';
              return (
                <span
                  key={s}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    state === 'current'
                      ? 'w-6 bg-scout-gold'
                      : state === 'done'
                        ? 'w-3 bg-scout-gold/50'
                        : 'w-3 bg-divider'
                  }`}
                />
              );
            })}
          </div>

          <span className="min-w-[120px] text-right font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim/50">
            {counter}
          </span>
        </div>

        {/* Animated screen body */}
        <div className="relative px-6 py-8 sm:px-9 sm:py-10">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={`${step}:${branch ?? 'fork'}`}
              custom={dir}
              variants={screen}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {body}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Honesty line — sets expectations while the CONTRIBUTOR funnel is dark.
          Member branch only: the builder branch feeds the live money path
          (/coverage → /apply), so there's no waitlist to caveat there. */}
      {branch === 'member' && !live && (
        <p className="mt-3 text-center font-body text-[11px] leading-relaxed text-parchment-dim/50">
          We’re opening this up gradually. Joining the list doesn’t grant access yet —
          it just saves your spot.
        </p>
      )}
    </div>
  );
}
